import React, { useCallback, useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";

import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { useAppSelector } from "app/hooks";
import AlertBar from "components/alert-bar";
import { APPOINTMENT_STATUS_ARRIVED } from "constants/appointments";
import { selectCurrentUser } from "features/auth/auth-slice";
import PatientDrawer, {
  usePatientDrawer
} from "features/patients/patient-drawer";
import PatientNotesDrawer from "features/patients/patient-notes-drawer";
import { QboReconnectBanner } from "features/quickbooks/qbo-reconnect-banner";
import StaffTasksV2Drawer from "features/staff-tasks-v2/drawer/staff-tasks-v2-drawer.component";
import useAlertBar from "hooks/use-alert-bar.hook";
import useArrivalNotificationSound from "hooks/use-arrival-notification-sound.hook";
import useClinicNotifications, {
  AppointmentStatusUpdateType
} from "hooks/use-clinic-notifications";
import { ContainerSizeProvider } from "hooks/use-container-size.hook";
import { usePatientPrivacy } from "hooks/use-patient-privacy.hook";
import {
  dispatchTabSyncMessage,
  syncChannel
} from "hooks/use-sync-between-tabs.hook";
import { Patient } from "services/types/patients.type";

import LayoutTopBar from "../layout-top-bar";
import PracticeContextBanner from "../practice-context-banner/practice-context-banner.component";

type LayoutMainContentProps = {
  isDrawerOpen: boolean;
};

// Tab-sync action so dismissing the arrival banner in one tab clears it in
// every open tab (Intercom 215475543630993).
export const DISMISS_ARRIVAL_ALERT_ACTION = "dismissArrivalAlert";

// Each tab's websocket delivers the arrival at its own pace, so a dismissal
// broadcast can reach a tab BEFORE that tab's arrival event does. Arrivals
// dismissed this recently are not shown at all.
export const ARRIVAL_DISMISSAL_RACE_WINDOW_MS = 10 * 1000;

export default function LayoutMainContent({
  isDrawerOpen
}: LayoutMainContentProps) {
  const { addMessageHandlers, removeMessageHandlers } =
    useClinicNotifications();
  const { addAlert, alerts, removeAlert } = useAlertBar();
  const currentUser = useAppSelector(selectCurrentUser);

  // Ids of alerts this bar knows to be arrival banners — only those take part
  // in cross-tab dismissal; a future non-arrival alert must not have its
  // dismissal broadcast under the arrival action.
  const arrivalAlertIdsRef = useRef(new Set<number>());

  // appointmentId → dismissedAt, for the websocket race above.
  const recentlyDismissedArrivalsRef = useRef(new Map<number, number>());

  const markArrivalDismissed = useCallback((appointmentId: number) => {
    const now = Date.now();
    recentlyDismissedArrivalsRef.current.forEach((dismissedAt, id) => {
      if (now - dismissedAt >= ARRIVAL_DISMISSAL_RACE_WINDOW_MS) {
        recentlyDismissedArrivalsRef.current.delete(id);
      }
    });
    recentlyDismissedArrivalsRef.current.set(appointmentId, now);
  }, []);

  const checkAppointmentPatientArrived = useCallback(
    (message: any) => {
      if (message?.type === AppointmentStatusUpdateType) {
        const {
          id,
          staff_member,
          new_status,
          patient,
          type,
          supervising_provider
        } = message.message;

        const isForPrimaryProvider = staff_member?.id === currentUser.id;
        const isForSupervisingProvider =
          Boolean(currentUser.supervising_provider_notifications_enabled) &&
          supervising_provider === currentUser.id;

        if (
          new_status === APPOINTMENT_STATUS_ARRIVED &&
          (isForPrimaryProvider || isForSupervisingProvider)
        ) {
          const dismissedAt = recentlyDismissedArrivalsRef.current.get(id);
          if (
            dismissedAt &&
            Date.now() - dismissedAt < ARRIVAL_DISMISSAL_RACE_WINDOW_MS
          ) {
            // Already acknowledged in another tab before this tab's websocket
            // delivered the arrival.
            return;
          }
          if (arrivalAlertIdsRef.current.has(id)) {
            // Re-arrival while the previous banner is still open: replace it
            // instead of stacking a duplicate alert id. Both updates land in
            // one render, so the alert element updates in place — the changed
            // eventKey is what retriggers the chime.
            removeAlert(id);
          }
          arrivalAlertIdsRef.current.add(id);
          addAlert({
            message: (
              <AppointmentPatientArrivedMessage
                patient={patient}
                type={type}
                appointmentId={id}
                eventKey={Date.now()}
              />
            ),
            severity: "warning",
            id
          });
        }
      }
    },

    [currentUser, addAlert, removeAlert]
  );

  useEffect(() => {
    const handlerKey = "appointment-arrive";
    addMessageHandlers(checkAppointmentPatientArrived, handlerKey);
    return () => removeMessageHandlers(handlerKey);
  }, [
    checkAppointmentPatientArrived,
    addMessageHandlers,
    removeMessageHandlers
  ]);

  // Dismissals are read straight off the sync channel rather than through
  // useSyncBetweenTabs: its emitterId self-filter drops messages between tabs
  // that SHARE an appInstanceId — sessionStorage (where that id lives) is
  // copied into tabs opened via the banner's own target="_blank" buttons and
  // duplicated tabs, exactly the tabs this sync must reach. BroadcastChannel
  // never delivers a message back to the posting tab, and removing an
  // already-removed alert is a no-op, so neither the self-filter nor the
  // messageId dedupe is needed here.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      let data: { action?: string; payload?: { appointmentId?: number } };
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      if (data?.action !== DISMISS_ARRIVAL_ALERT_ACTION) {
        return;
      }
      const appointmentId = data.payload?.appointmentId;
      if (appointmentId == null) {
        return;
      }
      markArrivalDismissed(appointmentId);
      if (arrivalAlertIdsRef.current.has(appointmentId)) {
        arrivalAlertIdsRef.current.delete(appointmentId);
        removeAlert(appointmentId);
      }
    };
    syncChannel.addEventListener("message", handleMessage);
    return () => syncChannel.removeEventListener("message", handleMessage);
  }, [removeAlert, markArrivalDismissed]);

  const handleRemoveAlert = useCallback(
    (id: number | string) => {
      removeAlert(id);
      if (typeof id === "number" && arrivalAlertIdsRef.current.has(id)) {
        arrivalAlertIdsRef.current.delete(id);
        markArrivalDismissed(id);
        dispatchTabSyncMessage({
          action: DISMISS_ARRIVAL_ALERT_ACTION,
          payload: { appointmentId: id }
        });
      }
    },
    [removeAlert, markArrivalDismissed]
  );

  const { isOpen, rightOffset } = usePatientDrawer();

  // The side menu sizes itself (100px rail, or fit-content expanded — ENG-2535), so the
  // content takes whatever is left rather than assuming the rail's old fixed width.
  return (
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <PracticeContextBanner />
      <LayoutTopBar />
      {/* Self-gating: renders nothing unless this user can reach QuickBooks and the practice's
          connection is actually broken. Mounted app-wide because a dead connection blocks syncing
          from every surface that creates a sale, not just the QuickBooks section. */}
      <QboReconnectBanner />
      {alerts.length > 0 && (
        <AlertBar alerts={alerts} removeAlert={handleRemoveAlert} />
      )}
      <ContainerSizeProvider
        sx={{
          width: `calc(100% - ${rightOffset}px)`,
          boxSizing: "border-box",
          overflow: "hidden",
          flex: 1,
          display: "flex"
        }}
      >
        <Outlet />
        {/* when isOpen==false we have to unmount PatientDrawer because it is using AppointmentsProvider inside */}
        {isOpen && <PatientDrawer />}
        <PatientNotesDrawer />
        <StaffTasksV2Drawer />
      </ContainerSizeProvider>
    </Stack>
  );
}

const AppointmentPatientArrivedMessage = ({
  patient,
  type,
  appointmentId,
  eventKey
}: {
  patient: Patient;
  type: any;
  appointmentId: number;
  // Identifies the arrival event this banner is for. A re-arrival replaces
  // the banner within one batched render (same alert id, no remount), so the
  // changed eventKey is what makes the chime play again.
  eventKey: number;
}) => {
  const theme = useTheme();
  const currentUser = useAppSelector(selectCurrentUser);

  useArrivalNotificationSound(
    Boolean(currentUser?.patient_arrived_sound_enabled),
    eventKey
  );

  const { isPrivateMode } = usePatientPrivacy();

  const patientName = (
    isPrivateMode
      ? [patient.first_name?.[0], patient.last_name?.[0]]
          .filter(Boolean)
          .join(".")
      : [patient.first_name, patient.last_name].join(" ")
  ).trim();

  const actionButtonSx = {
    color: theme.palette.common.white,
    borderColor: theme.palette.common.white,
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: theme.palette.common.white,
      backgroundColor: "rgba(255, 255, 255, 0.16)"
    }
  };

  return (
    <Stack direction="row" gap={1.5} alignItems="center">
      <Typography fontSize="inherit">{`Patient ${patientName} has arrived for ${type.name}`}</Typography>
      <Button
        variant="outlined"
        size="small"
        href={`/patients/${patient.id}/chart-notes`}
        target="_blank"
        sx={actionButtonSx}
      >
        Open Chart Note
      </Button>
      <Button
        variant="outlined"
        size="small"
        href={`/patients/${patient.id}/appointments/${appointmentId}`}
        target="_blank"
        sx={actionButtonSx}
      >
        Open Appointment
      </Button>
    </Stack>
  );
};
